import ListCreateButton from 'components/buttons/ListCreateButton';
import PageHeader from 'components/PageHeader';
import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import { ListViewsPageContext } from 'components/tables/list-table/filter-bar/builtInViews';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { FormattedMessage } from 'react-intl';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import TimecardListWithFilters from './TimecardListWithFilters.tsx';
import { useTimecardViews } from './useTimecardViews';

type TabType = 'main' | 'draft';

const TimecardListPage = (): JSX.Element => {
    const navigateWithReturn = useNavigateWithReturnState();
    const location = useLocation();
    const navigate = useNavigate();
    const isNew = useIsNewDesign();
    const views = useTimecardViews();

    const currentTab: TabType = location.pathname.includes('/timecards/draft') ? 'draft' : 'main';

    // /timecards (with or without a trailing slash, both of which the router matches to this
    // route) is the legacy entry point and the one the menu links to: redirect to the main tab
    // before the list renders. Redirecting from an effect instead would mount the list under
    // /timecards and only then change the pathname, which permanently trips the list's
    // "navigated away" guard: it stops writing filters to the URL and to the remembered-filter
    // entry, and reads the remembered filters back from the /timecards key instead of
    // /timecards/main. Legacy links carrying the pre-tab ismytimecard filter keep working.
    if (location.pathname.replace(/\/+$/, '') === '/timecards') {
        const searchParams = new URLSearchParams(location.search);
        const qfParam = searchParams.get('qf');
        if (qfParam?.includes('id=ismytimecard=true')) {
            searchParams.set('qf', qfParam.replace('id=ismytimecard=true', 'id=ismynondrafttimecard=true'));
        }
        const query = searchParams.toString();
        return <Navigate to={`/timecards/main${query ? `?${query}` : ''}`} replace />;
    }

    return (
        <PaddedLayout className="overflow-hidden">
            <div className="flex h-full flex-col gap-4">
                <PageHeader
                    hostsList
                    titleId="timecards.timecards"
                    buttons={
                        <div className="flex justify-end">
                            <ListCreateButton
                                permissions={['CREATE.TIMECARDS']}
                                variant="contained"
                                color="secondary"
                                size="small"
                                onClick={() => navigateWithReturn('/timecards/new')}
                                labelId="timecards.add-new-timecard"
                                newLabelId="new-entity.timecard"
                            />
                        </div>
                    }
                />
                {isNew ? null : (
                    <LineTabs
                        value={currentTab}
                        onValueChange={(value) => navigate(value === 'draft' ? '/timecards/draft' : '/timecards/main')}
                    >
                        <LineTabsList>
                            {views.map(({ id, labelId }) => (
                                <LineTab key={id} value={id}>
                                    <FormattedMessage id={labelId} />
                                </LineTab>
                            ))}
                        </LineTabsList>
                    </LineTabs>
                )}
                {/* In the new design the list renders the views row, MAIN and DRAFT included. */}
                <ListViewsPageContext.Provider
                    value={{
                        listKey: 'timecards',
                        views,
                        activeId: currentTab,
                        pathFor: (viewId) => `/timecards/${viewId}`,
                    }}
                >
                    {currentTab === 'main' && <TimecardListWithFilters tabType="main" />}
                    {currentTab === 'draft' && <TimecardListWithFilters tabType="draft" />}
                </ListViewsPageContext.Provider>
            </div>
        </PaddedLayout>
    );
};

export default TimecardListPage;
